/**
 * OpportunityCard
 *
 * Card component displaying a single enrichment opportunity with
 * attribute info, product counts, cost estimate, and action buttons.
 *
 * Part of Story 345: Enrichment Opportunities Dashboard
 *
 * Date: 2026-01-06
 */

import React from 'react';
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Search,
  MoreVertical,
  XCircle,
  Ban,
  CheckCircle2,
  Clock,
  DollarSign,
  Package,
} from 'lucide-react';
import { useToast } from '@/components/ui/use-toast';
import {
  EnrichmentOpportunity,
  formatCost,
  getSeverityColor,
  getRecommendationLabel,
  ignoreOpportunity,
  markAllUnavailable,
} from '@/services/enrichmentOpportunityService';

interface OpportunityCardProps {
  opportunity: EnrichmentOpportunity;
  onResearch: (opportunity: EnrichmentOpportunity) => void;
  onIgnore: (opportunityId: string) => void;
  onMarkAllNA: (opportunityId: string) => void;
}

export function OpportunityCard({
  opportunity,
  onResearch,
  onIgnore,
  onMarkAllNA,
}: OpportunityCardProps) {
  const { toast } = useToast();
  const [isActing, setIsActing] = React.useState(false);

  const handleIgnore = async () => {
    setIsActing(true);
    try {
      await ignoreOpportunity(opportunity.id);
      toast({
        title: 'Opportunity ignored',
        description: `The ${opportunity.attributeName} opportunity has been dismissed.`,
      });
      onIgnore(opportunity.id);
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to ignore opportunity',
        variant: 'destructive',
      });
    } finally {
      setIsActing(false);
    }
  };

  const handleMarkAllNA = async () => {
    setIsActing(true);
    try {
      const result = await markAllUnavailable(opportunity.id);
      toast({
        title: 'Products marked as N/A',
        description: `${result.markedCount} products marked as unavailable for ${opportunity.attributeName}.`,
      });
      onMarkAllNA(opportunity.id);
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to mark products as N/A',
        variant: 'destructive',
      });
    } finally {
      setIsActing(false);
    }
  };

  const isPending = opportunity.status === 'pending';
  const isCompleted = opportunity.status === 'completed';
  const isResearching = opportunity.status === 'researching';

  return (
    <Card className={`${opportunity.recommendation === 'recommended' ? 'border-primary/50' : ''}`}>
      <CardHeader className="pb-2">
        <div className="flex items-start justify-between">
          <div className="space-y-1">
            <CardTitle className="text-base font-semibold">
              {opportunity.attributeName.replace(/_/g, ' ')}
            </CardTitle>
            <div className="flex items-center gap-2">
              <Badge variant={getSeverityColor(opportunity.severity) as any}>
                {opportunity.severity}
              </Badge>
              {opportunity.recommendation === 'recommended' && (
                <Badge variant="default" className="bg-green-600">
                  Recommended
                </Badge>
              )}
              {opportunity.recommendation === 'optional' && (
                <Badge variant="secondary">Optional</Badge>
              )}
            </div>
          </div>

          {isPending && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="sm" disabled={isActing}>
                  <MoreVertical className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={handleIgnore}>
                  <XCircle className="h-4 w-4 mr-2" />
                  Ignore
                </DropdownMenuItem>
                <DropdownMenuItem onClick={handleMarkAllNA}>
                  <Ban className="h-4 w-4 mr-2" />
                  Mark All as N/A
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}

          {isCompleted && (
            <CheckCircle2 className="h-5 w-5 text-green-500" />
          )}

          {isResearching && (
            <Clock className="h-5 w-5 text-blue-500 animate-pulse" />
          )}
        </div>
      </CardHeader>

      <CardContent className="pb-3">
        {/* Product counts */}
        <div className="grid grid-cols-3 gap-2 text-sm mb-3">
          <div className="text-center p-2 bg-muted/50 rounded">
            <div className="font-semibold text-amber-600">{opportunity.premiumCount}</div>
            <div className="text-xs text-muted-foreground">Premium</div>
          </div>
          <div className="text-center p-2 bg-muted/50 rounded">
            <div className="font-semibold">{opportunity.midTierCount}</div>
            <div className="text-xs text-muted-foreground">Mid-Tier</div>
          </div>
          <div className="text-center p-2 bg-muted/50 rounded">
            <div className="font-semibold text-muted-foreground">{opportunity.entryCount}</div>
            <div className="text-xs text-muted-foreground">Entry</div>
          </div>
        </div>

        {/* Stats */}
        <div className="flex items-center justify-between text-sm">
          <div className="flex items-center gap-1 text-muted-foreground">
            <Package className="h-4 w-4" />
            <span>{opportunity.productCount} products</span>
          </div>
          <div className="flex items-center gap-1 font-medium">
            <DollarSign className="h-4 w-4" />
            <span>{formatCost(opportunity.estimatedCost)}</span>
          </div>
        </div>

        {/* Completed stats */}
        {isCompleted && opportunity.valuesFound !== undefined && (
          <div className="mt-3 p-2 bg-green-50 dark:bg-green-950 rounded text-sm">
            <div className="flex justify-between">
              <span>Values found:</span>
              <span className="font-medium text-green-600">{opportunity.valuesFound}</span>
            </div>
            {opportunity.notAvailableCount !== undefined && opportunity.notAvailableCount > 0 && (
              <div className="flex justify-between">
                <span>Not available:</span>
                <span className="text-muted-foreground">{opportunity.notAvailableCount}</span>
              </div>
            )}
            {opportunity.actualCost !== undefined && (
              <div className="flex justify-between">
                <span>Actual cost:</span>
                <span className="font-medium">{formatCost(opportunity.actualCost)}</span>
              </div>
            )}
          </div>
        )}
      </CardContent>

      {isPending && (
        <CardFooter className="pt-0">
          <Button
            className="w-full"
            onClick={() => onResearch(opportunity)}
            disabled={isActing}
          >
            <Search className="h-4 w-4 mr-2" />
            Research Now
          </Button>
        </CardFooter>
      )}
    </Card>
  );
}
